import {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import {bypassExpenseStateSelector} from '@app/screens/module-specific-utilities/pages/bypass-expense/redux/selectors';
import {bypassExpenseStateActions} from '@app/screens/module-specific-utilities/pages/bypass-expense/redux';
import {approveBypassExpensesCreator} from '@app/screens/module-specific-utilities/pages/bypass-expense/redux/slice';
import {strings} from '@app/common/strings';
import {monthMock} from '@app/components/widgets/placeholder/multiselect-placeholder';

const getMonthName = (month: number) =>
  monthMock().find(monthVal => monthVal.key === month)?.value ?? month;

const headerCellStyle = {
  fontFamily: 'Poppins',
  fontWeight: '600',
  fontSize: 12,
  color: '#747379',
  backgroundColor: '#f4f4f4',
  borderBottom: '2px solid #e0e0e0',
  padding: '10px 16px',
};

const bodyCellStyle = {
  fontFamily: 'Poppins',
  fontSize: 12,
  color: '#1c1939',
  padding: '10px 16px',
  borderBottom: '1px solid #f2f2f2',
};

export const ByPassExpenseGrid = ({filterParams}: {filterParams: any}) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const gridData: any[] = useSelector(
    bypassExpenseStateSelector.getBypassExpenseGridData(),
  );
  const gridLoading = useSelector(
    bypassExpenseStateSelector.getBypassExpenseGridLoading(),
  );
  const selectedExpenseIds: number[] = useSelector(
    bypassExpenseStateSelector.getBypassExpenseSelectedExpenseIds(),
  );
  const approveLoading = useSelector(bypassExpenseStateSelector.getIsLoading());

  useEffect(() => {
    setPage(0);
  }, [gridData]);

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPageSize(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  const handleToggleRow = useCallback(
    (expenseId: number) => {
      const isSelected = selectedExpenseIds.includes(expenseId);
      dispatch(
        bypassExpenseStateActions.setBypassExpenseSelectedExpenseIds(
          isSelected
            ? selectedExpenseIds.filter(id => id !== expenseId)
            : [...selectedExpenseIds, expenseId],
        ),
      );
    },
    [dispatch, selectedExpenseIds],
  );

  const handleToggleSelectAll = useCallback(() => {
    const allExpenseIds = gridData.map((row: any) => row.expenseId);
    const allSelected = allExpenseIds.every(id =>
      selectedExpenseIds.includes(id),
    );
    dispatch(
      bypassExpenseStateActions.setBypassExpenseSelectedExpenseIds(
        allSelected ? [] : allExpenseIds,
      ),
    );
  }, [dispatch, gridData, selectedExpenseIds]);

  const handleApprove = useCallback(() => {
    dispatch(
      approveBypassExpensesCreator({
        expenseIdList: selectedExpenseIds.join(','),
        filterParams,
      }),
    );
  }, [dispatch, selectedExpenseIds, filterParams]);

  if (gridLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!gridData.length) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography
          sx={{fontFamily: 'Poppins', fontSize: 14, color: '#747379'}}>
          No Records Found
        </Typography>
      </Box>
    );
  }

  const allSelected =
    gridData.length > 0 &&
    gridData.every((row: any) => selectedExpenseIds.includes(row.expenseId));
  const someSelected = selectedExpenseIds.length > 0 && !allSelected;
  const pagedGridData = gridData.slice(
    page * pageSize,
    page * pageSize + pageSize,
  );

  return (
    <Box sx={{margin: '8px 5px'}}>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Button
          data-testid="button-approve"
          variant="contained"
          disabled={selectedExpenseIds.length === 0 || approveLoading}
          onClick={handleApprove}>
          {strings.approve}
          {selectedExpenseIds.length > 0 ? ` (${selectedExpenseIds.length})` : ''}
        </Button>
      </Box>
      <Paper elevation={0} sx={{border: '1px solid #e0e0e0', borderRadius: 2}}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={headerCellStyle}>
                  <Checkbox
                    data-testid="checkbox-selectAll"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleToggleSelectAll}
                  />
                </TableCell>
                <TableCell sx={headerCellStyle}>EMP NAME</TableCell>
                <TableCell sx={headerCellStyle}>Mgr NAME</TableCell>
                <TableCell sx={headerCellStyle}>SAP CODE</TableCell>
                <TableCell sx={headerCellStyle}>DIVISION</TableCell>
                <TableCell sx={headerCellStyle}>STATE</TableCell>
                <TableCell sx={headerCellStyle}>HQ</TableCell>
                <TableCell sx={headerCellStyle}>Month & Year</TableCell>
                <TableCell sx={headerCellStyle}>EXPENSE STATUS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedGridData.map((row: any, index: number) => (
                <TableRow
                  key={row.expenseId ?? index}
                  sx={{'&:hover': {backgroundColor: '#f9f9f9'}}}>
                  <TableCell padding="checkbox" sx={bodyCellStyle}>
                    <Checkbox
                      data-testid="checkbox-selectRow"
                      checked={selectedExpenseIds.includes(row.expenseId)}
                      onChange={() => handleToggleRow(row.expenseId)}
                    />
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    {row.empName ?? '-'}
                  </TableCell>
                   <TableCell sx={bodyCellStyle}>
                    {row.managerName ?? '-'}
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    {row.sapCode ?? row.employeeCode ?? '-'}
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    {row.division ?? '-'}
                  </TableCell>
                   <TableCell sx={bodyCellStyle}>
                    {row.stateName ?? '-'}
                  </TableCell>
                   <TableCell sx={bodyCellStyle}>
                    {row.hqName ?? '-'}
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    {row.expenseMonth && row.expenseYear
                      ? `${getMonthName(row.expenseMonth)}-${row.expenseYear}`
                      : '-'}
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: 12,
                        color: '#1c1939',
                      }}>
                      {row.expenseStatus ?? '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={gridData.length}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{
            fontFamily: 'Poppins',
            fontSize: 12,
            borderTop: '1px solid #e0e0e0',
          }}
        />
      </Paper>
    </Box>
  );
};
